import request from './request'

/**
 * Get product list
 * @param {string} typeIds - Product type IDs, separated by commas, e.g. '2,3'
 * @param {number} page - Page number, default is 1
 * @param {number} limit - Number of items per page, default is 10
 * @returns {Promise} Product list data
 */
export const getProductList = (typeIds = '2,3', page = 1, limit = 10) => {
  return request({
    url: '/api/product/list',
    method: 'get',
    params: {
      type_ids: typeIds,
      page: page,
      limit: limit
    }
  })
}

/**
 * Get product details
 * @param {string} productId - Product ID
 * @returns {Promise} Product details data
 */
export const getProductDetail = (productId) => {
  return request({
    url: '/api/product/detail',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get product parameters
 * @param {string} productId - Product ID
 * @returns {Promise} Product parameters data
 */
export const getProductParams = (productId) => {
  return request({
    url: '/api/product/param',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get product specifications list
 * @param {string} productId - Product ID
 * @returns {Promise} Product specifications data
 */
export const getProductSpecs = (productId) => {
  return request({
    url: '/api/product/specs',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get product SKU list
 * @param {string} productId - Product ID
 * @returns {Promise} Product SKU data
 */
export const getProductSkus = (productId) => {
  return request({
    url: '/api/product/skus',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get corresponding SKU based on specification values
 * @param {string} productId - Product ID
 * @param {Array} specValueIds - Specification value ID array
 * @returns {Promise} SKU data
 */
export const getSkuBySpecs = (productId, specValueIds) => {
  return request({
    url: '/api/product/sku-by-specs',
    method: 'post',
    data: {
      product_id: productId,
      spec_value_ids: specValueIds
    }
  })
}

/**
 * Get product information based on product ID and specification values (name, specifications, price, images)
 * @param {string} productId - Product ID
 * @param {Array} specValueIds - Specification value ID array
 * @returns {Promise} Product information data
 */
export const getProductInfoBySpecs = (productId, specValueIds) => {
  return request({
    url: '/api/product/product-info-by-specs',
    method: 'get',
    params: {
      product_id: productId,
      spec_value_ids: specValueIds
    }
  })
}

/**
 * Get product customization parameters
 * @param {string} productId - Product ID
 * @returns {Promise} Customization parameters list
 */
export const getProductCustomParams = (productId) => {
  return request({
    url: '/api/product/custom-params',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Submit custom order
 * @param {Object} data - Custom order data
 * @returns {Promise} Submission result
 */
export const submitCustomOrder = (data) => {
  return request({
    url: '/api/product/submit-custom-order',
    method: 'post',
    data
  })
}



/**
 * Get product size chart data
 * @param {string} productId - Product ID
 * @returns {Promise} Size chart data
 */
export const getProductSizeChart = (productId) => {
  return request({
    url: '/api/product/size-chart',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get product size parameters list
 * @param {string} productId - Product ID
 * @returns {Promise} Size parameters list
 */
export const getProductSizeParams = (productId) => {
  return request({
    url: '/api/product/size-params',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get product size detail chart data
 * @param {string} productId - Product ID
 * @returns {Promise} Size detail chart data
 */
export const getProductSizeDetailChart = (productId) => {
  return request({
    url: '/api/product/size-detail-chart',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get product size detail parameters list
 * @param {string} productId - Product ID
 * @returns {Promise} Size detail parameters list
 */
export const getProductSizeDetailParams = (productId) => {
  return request({
    url: '/api/product/size-detail-params',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get product size detail combinations list
 * @param {string} productId - Product ID
 * @returns {Promise} Size detail combinations list
 */
export const getProductSizeDetailCombinations = (productId) => {
  return request({
    url: '/api/product/size-detail-combinations',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}

/**
 * Get product size combinations list
 * @param {string} productId - Product ID
 * @returns {Promise} Size combinations list
 */
export const getProductSizeCombinations = (productId) => {
  return request({
    url: '/api/product/size-combinations',
    method: 'get',
    params: {
      product_id: productId
    }
  })
}